import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Sparkles, X, Send, RotateCcw } from "lucide-react";
import { API, api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ActionCard } from "@/components/assistant/ActionCard";

const UNAVAILABLE = "Assistant is unavailable — call 214-506-2525 or open a support ticket";

const visitorId = () => {
  let v = localStorage.getItem("lux_visitor_id");
  if (!v) { v = `v-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`; localStorage.setItem("lux_visitor_id", v); }
  return v;
};

const Rich = ({ text }) => text.split(/(\*\*[^*]+\*\*)/g).map((p, i) => (p.startsWith("**") ? <strong key={i} className="text-amber-100">{p.slice(2, -2)}</strong> : <span key={i}>{p}</span>));

const Bubble = ({ m }) => (
  <div data-testid={`ai-message-${m.role}`} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
    <div className={`max-w-[88%] rounded-2xl px-3.5 py-2.5 text-sm ${m.role === "user" ? "bg-amber-400/15 text-zinc-100" : m.error ? "border border-red-500/40 bg-red-500/10 text-red-200" : "bg-white/[0.05] text-zinc-200"}`}>
      <p className="whitespace-pre-wrap break-words" data-testid={m.error ? "ai-error" : undefined}><Rich text={m.content || ""} /></p>
      {(m.actions || []).map((a) => <ActionCard key={a.id} action={a} />)}
      {(m.tickets || []).length > 0 && <p data-testid="ai-ticket-created" className="mt-2 text-xs text-emerald-300">Support ticket created — see Customer Service.</p>}
    </div>
  </div>
);

async function streamChat(message, onEvent) {
  const token = localStorage.getItem("lux_token");
  const res = await fetch(`${API}/ai/chat`, {
    method: "POST",
    headers: { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    body: JSON.stringify({ message, visitor_id: visitorId() }),
  });
  if (!res.ok || !res.body) throw new Error("bad response");
  const reader = res.body.getReader();
  const dec = new TextDecoder();
  let buf = "";
  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    buf += dec.decode(value, { stream: true });
    const parts = buf.split("\n\n");
    buf = parts.pop();
    parts.forEach((p) => p.startsWith("data: ") && onEvent(JSON.parse(p.slice(6))));
  }
}

export const AssistantWidget = () => {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const end = useRef(null);

  useEffect(() => {
    if (!open || user === null) return;
    api.get("/ai/history", { params: { visitor_id: visitorId() } }).then(({ data }) => setMessages(data.messages)).catch(() => {});
  }, [open, user]);
  useEffect(() => { end.current?.scrollIntoView({ block: "end" }); }, [messages, open]);

  const send = async (e) => {
    e.preventDefault();
    const msg = text.trim();
    if (!msg || busy) return;
    setText("");
    setBusy(true);
    setMessages((xs) => [...xs, { id: `u${Date.now()}`, role: "user", content: msg }, { id: "pending", role: "assistant", content: "", actions: [], tickets: [] }]);
    const patch = (fn) => setMessages((xs) => xs.map((m) => (m.id === "pending" ? fn(m) : m)));
    try {
      await streamChat(msg, (ev) => {
        if (ev.type === "delta") patch((m) => ({ ...m, content: m.content + ev.text }));
        if (ev.type === "action") patch((m) => ({ ...m, actions: [...m.actions, ev.action] }));
        if (ev.type === "ticket") patch((m) => ({ ...m, tickets: [...m.tickets, ev.ticket_id] }));
        if (ev.type === "error") patch((m) => ({ ...m, content: ev.message, error: true }));
      });
    } catch {
      patch((m) => ({ ...m, content: UNAVAILABLE, error: true }));
    } finally {
      setMessages((xs) => xs.map((m) => (m.id === "pending" ? { ...m, id: `a${Date.now()}` } : m)));
      setBusy(false);
    }
  };
  const reset = async () => { await api.post("/ai/reset", null, { params: { visitor_id: visitorId() } }); setMessages([]); };
  if (user?.role === "business" || window.location.pathname.startsWith("/track/")) return null;

  return (
    <>
      {!open && (
        <button data-testid="ai-assistant-button" onClick={() => setOpen(true)} aria-label="Open assistant"
          className="fixed bottom-4 right-4 z-[1250] flex h-14 w-14 items-center justify-center rounded-full bg-gradient-to-br from-amber-200 via-amber-400 to-amber-700 text-black shadow-[0_10px_40px_-10px_rgba(212,175,55,0.8)] transition-transform duration-300 hover:-translate-y-0.5 sm:bottom-6 sm:right-6">
          <Sparkles className="h-6 w-6" />
        </button>
      )}
      {open && (
        <div data-testid="ai-assistant-panel" className="fixed inset-x-2 bottom-2 top-16 z-[1250] flex flex-col overflow-hidden rounded-2xl border border-amber-500/25 bg-[#0e0e12]/95 shadow-2xl backdrop-blur-xl sm:inset-x-auto sm:bottom-6 sm:right-6 sm:top-auto sm:h-[620px] sm:w-[400px]">
          <div className="flex items-center gap-3 border-b border-white/5 px-4 py-3">
            <Sparkles className="h-5 w-5 text-amber-400" />
            <div className="flex-1"><p className="font-serif text-amber-50">Luxury Concierge</p><p className="text-[11px] text-zinc-500">AI assistant · 214-506-2525</p></div>
            <button data-testid="ai-reset-button" onClick={reset} className="rounded-full p-2 text-zinc-500 hover:text-amber-200" aria-label="New conversation"><RotateCcw className="h-4 w-4" /></button>
            <button data-testid="ai-close-button" onClick={() => setOpen(false)} className="rounded-full p-2 text-zinc-400 hover:text-amber-200" aria-label="Close"><X className="h-5 w-5" /></button>
          </div>
          <div className="flex-1 space-y-3 overflow-y-auto px-3 py-4" data-testid="ai-messages">
            {messages.length === 0 && <p className="px-2 text-sm text-zinc-400">Ask about prices, policies, your trips or support. {user ? "I can also prepare actions for you to confirm." : ""}</p>}
            {messages.map((m) => <Bubble key={m.id} m={m} />)}
            {busy && messages.at(-1)?.content === "" && <p data-testid="ai-typing" className="px-2 text-xs text-zinc-500">Thinking…</p>}
            <div ref={end} />
          </div>
          {!user && <p data-testid="ai-signin-prompt" className="border-t border-white/5 px-4 py-2 text-xs text-zinc-400"><Link to="/login" onClick={() => setOpen(false)} className="text-amber-300">Sign in to manage your rides</Link></p>}
          <form onSubmit={send} className="flex gap-2 border-t border-white/5 p-3">
            <input data-testid="ai-input" className="lux-input min-w-0 flex-1" placeholder="Ask anything…" value={text} maxLength={2000} onChange={(e) => setText(e.target.value)} />
            <button type="submit" data-testid="ai-send-button" disabled={!text.trim() || busy} className="btn-gold !px-4" aria-label="Send"><Send className="h-4 w-4" /></button>
          </form>
        </div>
      )}
    </>
  );
};
